import { apiGet, cancelSelectEvent, changeSymbolForWidget, defaultIntervalOptions, getAlignedTimestampFromInterval, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getIntervalAndSubpointsFromPeriod, getNoDataHTML, getStartDateFromPeriod, getSymbolDropdown, getSymbolOptions, getTraderSMCardHTML, getWebsocket, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, periodDefaultValue, pickCandlestickExportConfig, setupHeaderPin, toggleWidgetTheme, updateSelectedSymbolInDropdown , chartWindowStart, chartWindowEnd } from './../../helpers';
import { CandleStickData, CandlestickChartConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

const global = window as any;

export class TraderverseCandleStickChart {
  private container: HTMLElement;
  private config: CandlestickChartConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private currentData: any[] | null = null;
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private apiHost: string = "https://facts.traderverse.io/markets/api/v3/chartapi";
  private isChartLoaded: boolean = false;
  private chartData: any[][] = [];
  private volumeData: number[] = [];
  private chartLabels: string[] = [];
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  // Increase default zoom so charts appear more zoomed-in to recent candles
  private defaultZoomStart: number = 98; // Show last ~2% of data (more zoomed to latest)
  private defaultZoomEnd: number = 100;
  private autoZoomToLatest: boolean = true;
  private autoZoomCandleCount: number = 30; // Show fewer candles for thicker appearance
  private widgetIndex: number = 0;

  private socketHandler: ((data: any) => void) | null = null;
  private maxDataPoints: number = 5000; // Limit data array size to prevent memory bloat
  private updateThrottleMs: number = 100; // Throttle chart updates to 10fps max
  private lastUpdateTime: number = 0;
  private pendingUpdate: boolean = false;
  private batchTimeout: number | null = null;


  private async setupWebSocket() {
    // Ensure websocket is initialized. Actual per-instance listener registration
    // is handled by the centralized dispatcher to avoid many socket listeners
    // when multiple widgets are rendered.
    await getWebsocket();
    this.registerInstanceForGlobalSocket();
  }

  // Public entry point used by the centralized socket dispatcher
  public receiveSocketData(data: any): void {
    try {
      this.handleOHLCVUpdate(data);
    } catch (e) {
      // swallow to avoid breaking global dispatcher
    }
  }

  private registerInstanceForGlobalSocket(): void {
    const win = window as any;
    if (!win.__TraderverseCandleStickChartInstances) {
      win.__TraderverseCandleStickChartInstances = new Set();
    }

    win.__TraderverseCandleStickChartInstances.add(this);

    // Install a single global socket handler once
    if (!win.__TraderverseCandleStickChartSocketRegistered) {
      const install = () => {
        if (!win.socket) return;
        win.__TraderverseCandleStickChartSocketHandler = (rawData: any) => {
          // parse once and dispatch to matching instances
          let parsed: any;
          try {
            parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
          } catch (e) {
            return;
          }
          // Dispatch only to instances that are interested (fast check on symbol)
          win.__TraderverseCandleStickChartInstances.forEach((inst: any) => {
            // quick guards to avoid method calls when not needed
            if (!inst || !inst.isChartLoaded || !inst.currentData || inst.currentData.length === 0) return;
            const currentSymbol = inst.currentData[0].symbol;
            if (currentSymbol !== parsed.symbol) return;
            try {
              inst.receiveSocketData(parsed);
            } catch (e) {
              // continue on error
            }
          });
        };
        win.socket.on('tradeupdate', win.__TraderverseCandleStickChartSocketHandler);
        win.__TraderverseCandleStickChartSocketRegistered = true;
      };

      // If socket already exists install immediately; otherwise wait for it
      if (win.socket && win.socket.on) {
        install();
      } else {
        // try to install once socket becomes available
        setTimeout(() => {
          if (win.socket && win.socket.on) install();
        }, 100);
      }
    }
  }


  private handleOHLCVUpdate(data: any): void {
    if (!this.isChartLoaded || !this.currentData?.length) {
      return; // Silent skip for better performance
    }

    // Parse data only once
    const parsedData = typeof data === 'string' ? JSON.parse(data) : data;

    // Check symbol match - be more flexible with symbol comparison
    const currentSymbol = this.currentData[0].symbol;
    if (currentSymbol !== parsedData.symbol) {
      return; // Silent skip for better performance
    }

    this.processUpdate(parsedData);
  }

  private processUpdate(parsedData: any): void {

    // Convert UTC timestamp to local time (optimized)
    let timestamp: Date;
    if (parsedData.ts) {
      timestamp = new Date(parsedData.ts);
    } else {
      timestamp = new Date();
    }

    // Validate timestamp (simplified check)
    if (!this.isValidTimestamp(timestamp.getTime())) {
      timestamp = new Date();
    }

    const newTime = getAlignedTimestampFromInterval(timestamp.getTime(), this.config.interval || '1D');
    // Determine if we should create a new candle or update existing one
    const { shouldCreate: shouldCreateNewCandle, updateIndex: indexToUpdate } = this.shouldCreateNewCandle(newTime);


    if (shouldCreateNewCandle) {
      const ohlc = {
        symbol: parsedData.symbol,
        open: parsedData.price,
        high: parsedData.price,
        low: parsedData.price,
        close: parsedData.price,
        volume: parsedData.size || parsedData.volume || 0,
        timestamp: newTime.toISOString(),
        time: newTime.getTime() / 1000
      };
      // Find correct position to insert new candle chronologically
      const insertIndex = this.findInsertIndex(newTime);

      // Insert new candle at correct position
      if (this.currentData) {
        this.currentData.splice(insertIndex, 0, ohlc);
        this.chartLabels.splice(insertIndex, 0, newTime.toISOString());
        this.chartData.splice(insertIndex, 0, [
          ohlc.open,
          ohlc.close,
          ohlc.low,
          ohlc.high
        ]);
        this.volumeData.splice(insertIndex, 0, ohlc.volume);
      }

      // Enforce maximum data points to prevent memory bloat
      this.trimDataArrays();

      // Removed verbose logging for performance
    } else if (this.currentData) {
      // Update existing candle (optimized)
      const ohlc = this.currentData[indexToUpdate];
      ohlc.high = Math.max(ohlc.high, parsedData.price);
      ohlc.low = Math.min(ohlc.low, parsedData.price);
      ohlc.close = parsedData.price;

      // Handle volume updates (optimized)
      if (parsedData.size) {
        ohlc.volume += parsedData.size;
      } else if (parsedData.volume !== undefined) {
        ohlc.volume = parsedData.volume;
      }

      // Update chart arrays (optimized - direct assignment)
      this.chartData[indexToUpdate] = [
        ohlc.open,
        ohlc.close,
        ohlc.low,
        ohlc.high
      ];
      this.volumeData[indexToUpdate] = ohlc.volume;
      // No need to reassign the whole object since we modified in place

      // Removed verbose logging for performance
    }

    // Use throttled update to prevent excessive redraws
    this.scheduleChartUpdate();
  }

  private trimDataArrays(): void {
    if (!this.currentData || this.currentData.length <= this.maxDataPoints) {
      return;
    }

    const excessCount = this.currentData.length - this.maxDataPoints;

    // Remove oldest data points from the beginning of arrays
    this.currentData.splice(0, excessCount);
    this.chartLabels.splice(0, excessCount);
    this.chartData.splice(0, excessCount);
    this.volumeData.splice(0, excessCount);

  }

  private scheduleChartUpdate(): void {
    const now = Date.now();

    // If enough time has passed since last update, update immediately
    if (now - this.lastUpdateTime >= this.updateThrottleMs) {
      this.performChartUpdate();
      this.lastUpdateTime = now;
      return;
    }

    // Otherwise, schedule a throttled update
    if (!this.pendingUpdate) {
      this.pendingUpdate = true;
      const remainingTime = this.updateThrottleMs - (now - this.lastUpdateTime);

      setTimeout(() => {
        this.performChartUpdate();
        this.lastUpdateTime = Date.now();
        this.pendingUpdate = false;
      }, remainingTime);
    }
  }

  private performChartUpdate(): void {
    if (!this.currentChart || !this.isChartLoaded) {
      return;
    }

    try {
      // Pre-calculate formatted time labels to avoid repeated computation
      const formattedLabels = this.chartLabels.map((iso: string) => {
        const d = new Date(iso);
        return this.formatLocalTime(d);
      });

      // Pre-calculate volume data with colors
      const volumeSeriesData = this.volumeData.map((volume, index) => {
        const candleData = this.chartData[index];
        const isUp = candleData && candleData[1] >= candleData[0];
        return {
          value: volume,
          itemStyle: {
            color: isUp ? '#00FF5750' : '#FF000050'
          }
        };
      });

      this.currentChart.setOption({
        series: [
          {
            name: 'Candlestick',
            type: 'candlestick',
            data: this.chartData,
            xAxisIndex: 0,
            yAxisIndex: 0,
            itemStyle: {
              color: '#00FF57',
              color0: '#FF0000',
              borderColor: '#00FF57',
              borderColor0: '#FF0000'
            },
            barGap: '0%',
            barCategoryGap: '10%'
          },
          {
            name: 'Volume',
            type: 'bar',
            data: volumeSeriesData,
            xAxisIndex: 1,
            yAxisIndex: 1,
            barGap: '0%',
            barCategoryGap: '10%'
          }
        ],
        xAxis: [
          {
            type: 'category',
            data: formattedLabels
          },
          {
            type: 'category',
            data: formattedLabels
          }
        ]
      }, false); // notMerge: false to update existing data

      this.emit('dataUpdate', this.chartData);
    } catch (error) {
      console.error('Error updating chart:', error);
    }
  }


  constructor(config: CandlestickChartConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.apiHost = this.config.apiHost ?? this.apiHost;
    this.container = this.resolveContainer(config.container);
    // Create shadow DOM for style isolation (like TradingView)
    // If shadowRoot already exists, reuse it; otherwise, attach a new one
    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    // Observe .traderverse-widget for resize
    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    // Wait for widget to be rendered
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) {
        this.widgetResizeObserver?.observe(widget);
      }
    }, 0);
  }

  private getDefaultConfig(): Partial<CandlestickChartConfig> {
    let symbols = getSymbolOptions();
    return {
      headerPinned: true,
      symbol: symbols[0].stockId,
      theme: 'default-dark',
      width: '100%',
      interval: '1D',
      period: '5Y',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const element = document.getElementById(container) || document.querySelector(container);
      if (!element) {
        console.error(`Container not found: ${container}`);
        throw new Error(`Container not found: ${container}`);
      }
      return element as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) {
      this.config.symbol = getSymbolOptions()[0].stockId;
    } else if (!this.config.symbol.includes("_")) {
      this.config.symbol = `${this.config.symbol}_NASDAQ`;
    }
    await this.setupWebSocket();
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    this.attachStyles();
    this.setupEventListeners();
    this.renderDropdownOptions();
    await this.injectChartLibrary();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    const periodOptions = [
      "1D",
      "5D",
      "1M",
      "3M",
      "6M",
      "1Y",
      "5Y",
    ].map(s => {
      const selected = this.config.period === s ? 'selected' : '';
      return `<option value="${s}" ${selected}>${s}</option>`;
    }).join('');
    const showPeriod = !this.config.hidePeriod;
    const showInterval = !this.config.hideInterval;
    const showFiltersUi = showPeriod || showInterval;
    const filtersSectionHtml = showFiltersUi
      ? `

              <div class="filtersBox sideBar hidden">
                ${showPeriod ? `
                <div class="item">
                  <select class="period-dropdown" data-testing-id="period-dropdown">
                    ${periodOptions}
                  </select>
                </div>` : ''}
                ${showInterval ? `
                <div class="item">
                  <select class="interval-dropdown" data-testing-id="interval-dropdown">
                  </select>
                </div>` : ''}
              </div>`
      : '';
    let themeClass = 'defaultDarkMode';
    let dataTheme: 'default-dark' | 'default-lite' | 'white' | 'black' = 'default-dark';
    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
    <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Candlestick Chart</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div>
            </div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
              ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
              <div class="trader-sm-card" id="selected-symbol">
              ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!.split("_")[0], type: "stock", name: "" })}
                </div>
                <span id="symbol-dropdown-placeholder"></span>
              </div>`}
              <span></span>
              ${filtersSectionHtml}
            </div>

            <div class="chartDv" id="tv_container">
            
            </div>

          </div>
        </div>
    `;

    const chartHtml = `
      <div class="pricePerformanceMain">
        <div id="candlestickChart" class="chart">${widgetHtml}</div>
    </div>
    
    `

    this.shadowRoot.innerHTML = chartHtml;

    // Insert the symbol dropdown element if enabled (and symbol not hidden)
    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }


  private renderDropdownOptions() {
    const intervalDropdown = this.shadowRoot.querySelector('.interval-dropdown') as HTMLSelectElement;
    const configuredPeriod = this.config.period as keyof typeof defaultIntervalOptions;
    const periodKey: keyof typeof defaultIntervalOptions =
      configuredPeriod && configuredPeriod in defaultIntervalOptions ? configuredPeriod : '1M';
    const intervals = defaultIntervalOptions[periodKey];
    const intervalOptions = intervals.map(f => {
      const selected = this.config.interval === f ? 'selected' : '';
      return `<option value="${f}" ${selected}>${f}</option>`;
    }).join('');
    if (intervalDropdown) {
      intervalDropdown.innerHTML = intervalOptions;
    }
  }



  onSelectChange(stock: any) {
    this.changeSymbol(stock.stockId, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement | null;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
    if (filtersToggle && filtersBox) {
      filtersToggle.addEventListener('click', (ev: Event) => {
        ev.preventDefault();
        filtersBox.classList.toggle('hidden');
      });
    }

    const periodDropdown = this.shadowRoot.querySelector('.period-dropdown') as HTMLSelectElement;
    periodDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      // this dropdown controls the period, not the interval
      (this.config as any).period = value;
      this.config.interval =
        periodDefaultValue[value as keyof typeof periodDefaultValue];
      this.renderDropdownOptions();
      this.config.onPeriodChange?.(value);
      this.setupChart(this.config.symbol!);
    });
    if (periodDropdown) {
      periodDropdown.value = (this.config as any).period || '1M';
    }

    // Frequency / interval dropdown (e.g. 1m, 5m, 1h...)
    const frequencyDropdown = this.shadowRoot.querySelector('.interval-dropdown') as HTMLSelectElement;
    frequencyDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      (this.config as any).interval = value;
      this.config.onIntervalChange?.(value);
      this.renderDropdownOptions();
      this.setupChart(this.config.symbol!);
    });
    if (frequencyDropdown) {
      frequencyDropdown.value = (this.config as any).interval || '1D';
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    // Load echarts once on the main document to avoid duplicate script tags
    return new Promise<boolean>((resolve) => {
      const win = window as any;
      if (win.echarts) return resolve(true);

      // If we've already started loading echarts, wait for it
      if (win.__TraderverseEchartsLoadingPromise) {
        win.__TraderverseEchartsLoadingPromise.then(() => resolve(true)).catch(() => resolve(false));
        return;
      }

      win.__TraderverseEchartsLoadingPromise = new Promise<void>((res) => {
        const existing = document.querySelector('script[data-chart-lib]') as HTMLScriptElement | null;
        if (existing) {
          existing.addEventListener('load', () => res());
          existing.addEventListener('error', () => res());
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        script.addEventListener('load', () => res());
        script.addEventListener('error', () => res());
        document.head.appendChild(script);
      }).then(() => true).catch(() => false);

      win.__TraderverseEchartsLoadingPromise.then((v: any) => resolve(!!(window as any).echarts));
    });
  }

  private requestStocksDataTimeout: number | null = null;
  private requestStocksData(symbol: string): void {
    // Use a shared polling mechanism per symbol to avoid many timers
    const win = window as any;
    if (!win.__TraderverseStockPollers) win.__TraderverseStockPollers = {};

    const pollers = win.__TraderverseStockPollers;

    // If this instance previously registered a different symbol, ensure cleanup is handled in destroy
    if (!pollers[symbol]) {
      pollers[symbol] = { instances: new Set(), intervalId: null };
    }

    pollers[symbol].instances.add(this);

    // Start polling only once per symbol
    if (!pollers[symbol].intervalId) {
      const poll = () => {
        try {
          if (global.socket && global.socket.connected) {
            global.socket.emit(
              'getstocksdata',
              JSON.stringify({ stocks: [{ stockId: symbol, showTrendChart: true }] })
            );
          }
        } catch (e) {
          // ignore
        }
      };
      // Poll every 2s to reduce load when many widgets present
      pollers[symbol].intervalId = window.setInterval(poll, 2000) as any;
      // Initial immediate poll
      poll();
    }
  }

  private async setupChart(stockId: string): Promise<void> {
    this.isChartLoaded = false;
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    
    if (this.currentChart) {
      const echarts = (window as any).echarts;
      if (echarts) {
        try {
          echarts.dispose(this.currentChart);
        } catch (e) {
          console.warn('Error disposing previous chart:', e);
        }
      }
      this.currentChart = null;
    }

    const container = this.shadowRoot.querySelector('#tv_container') as HTMLCanvasElement;
    if (container) {
      container.innerHTML = ''; // Clear previous content
    }


    // Get current date in UTC
    const nowUTC = new Date();

    // Example: Set startDate to start of today in UTC
    // Determine startDate and endDate based on this.config.period
    const startDate: Date = getStartDateFromPeriod(this.config.period || '1M');
    const endDate: Date = new Date(Date.UTC(
      nowUTC.getUTCFullYear(),
      nowUTC.getUTCMonth(),
      nowUTC.getUTCDate(),
      23, 59, 59, 999
    ));

    const { interval, subPoints } = getIntervalAndSubpointsFromPeriod(this.config.interval || '1D');
    const effectiveStartDate =
      interval === 'minute'
        ? new Date(startDate.getTime() - 24 * 60 * 60 * 1000)
        : startDate;

    const symbol = this.config.symbol ?? '';
    const url = `${this.apiHost}/prices`;
    const query: Record<string, string | number> = {
      symbol,
      interval,
      sub_points: subPoints,
      start_date: chartWindowStart(effectiveStartDate),
      end_date: chartWindowEnd(endDate),
    };
    try {
      const response = await apiGet({
        url,
        query,
      });
      const data = await response.json();

      this.currentData = data.data
        .map((item: any) => {
          // Convert UTC timestamp to local time
          const localTime = new Date(item.timestamp);
          item.time = localTime.getTime() / 1000;
          item.timestamp = localTime.toISOString();
          return item;
        })
        .filter((item: any) => {
          // Use local time for weekend filtering
          const day = new Date(item.time * 1000).getDay();
          return day !== 0 && day !== 6; // remove weekends
        })
        .reverse(); // Reverse to show earliest date on left, latest on right

      this.requestStocksData(stockId);

      if (this.currentData && this.currentData.length > 0) {
        this.chartLabels = this.currentData.map(x =>
          new Date(x.time * 1000).toISOString()
        );

        this.chartData = this.currentData.map(x => [
          x.open,
          x.close,
          x.low,
          x.high
        ]);

        this.volumeData = this.currentData.map(x => x.volume || 0);
      } else {
        widgetBox?.removeAttribute('data-testing-id');
        this.chartLabels = [];
        this.chartData = [];
        this.volumeData = [];
      }
      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (error) {
      console.log("Error fetching chart data:", error);
      widgetBox?.removeAttribute('data-testing-id');
    }
  }

  private updateDisplay(): void {
    if (!this.currentData) return;
    if (!(window as any).echarts) {
      // Try again after a short delay
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.isChartLoaded = false;
    this.drawChart();
  }

  private drawChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#tv_container') as HTMLElement;
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    
    if (!container) {
  

      throw new Error('Chart container not found');
    }
if (this.currentData && this.currentData.length > 0) {
  container.setAttribute('data-testing-id', 'candlestick-chart');
} else {
  container.removeAttribute('data-testing-id');
}

    // Properly dispose any existing chart instance
    if (this.currentChart) {
      try {
        echarts.dispose(this.currentChart);
      } catch (e) {
        console.warn('Error disposing current chart:', e);
      }
      this.currentChart = null;
    }

    // Also check for any orphaned instances
    const instance = echarts.getInstanceByDom(container);
    if (instance) {
      try {
        echarts.dispose(instance);
      } catch (e) {
        console.warn('Error disposing orphaned chart instance:', e);
      }
    }

    container.innerHTML = ''; // Clear previous chart

    // Check if there's data to display
    if (!this.currentData || this.currentData.length === 0) {
      widgetBox?.removeAttribute('data-testing-id');
      container.innerHTML = getNoDataHTML();
      return;
    }

    const upColor = '#00FF57';
    const downColor = '#FF0000';

    // Precompute formatted labels once to avoid repeated Date conversions
    const formattedLabels = this.chartLabels.map((iso: string) => this.formatLocalTime(new Date(iso)));

    const option: any = {
      backgroundColor: 'transparent',
      grid: [
        { left: '8%', right: '5%', top: '5%', height: '65%', containLabel: true },
        { left: '8%', right: '5%', top: '75%', height: '20%', containLabel: true }
      ],
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: [0, 1],
          start: this.defaultZoomStart,
          end: this.defaultZoomEnd,
          // allow very fine-grained zooming
          minSpan: 1,
          maxSpan: 100
        },
        {
          type: 'slider',
          xAxisIndex: [0, 1],
          start: this.defaultZoomStart,
          end: this.defaultZoomEnd,
          top: '97%',
          height: 20,
          borderColor: '#ccc',
          fillerColor: 'rgba(167, 183, 204, 0.4)',
          handleStyle: {
            color: '#a7b7cc'
          },
          textStyle: {
            color: '#9aa0a6'
          }
        }
      ],
      brush: {
        toolbox: ['rect', 'polygon', 'lineX', 'lineY', 'keep', 'clear'],
        xAxisIndex: [0, 1]
      },
      xAxis: [
        {
          type: 'category',
          data: formattedLabels,
          boundaryGap: true,
          axisLine: { show: true, lineStyle: { color: '#444' } },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            color: '#9aa0a6',
            fontSize: 11,
            showMinLabel: true,
            showMaxLabel: true,
            rotate: 0
          },
          gridIndex: 0
        },
        {
          type: 'category',
          data: formattedLabels,
          boundaryGap: true,
          axisLine: { show: true, lineStyle: { color: '#444' } },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            color: '#9aa0a6',
            fontSize: 11,
            showMinLabel: true,
            showMaxLabel: true
          },
          gridIndex: 1
        }
      ],
      yAxis: [
        {
          position: 'right',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#333',
              type: 'dashed',
              opacity: 0.3
            }
          },
          splitNumber: 5,
          axisLabel: {
            color: '#9aa0a6',
            fontSize: 11,
            inside: false
          },
          scale: true,
          gridIndex: 0
        },
        {
          position: 'right',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            color: '#9aa0a6',
            fontSize: 9,
            inside: false,
            margin: 8,
            formatter: (value: number) => {
              if (value >= 1000000) {
                return (value / 1000000).toFixed(0) + 'M';
              } else if (value >= 1000) {
                return (value / 1000).toFixed(0) + 'K';
              }
              return value.toString();
            }
          },
          splitNumber: 3,
          scale: true,
          gridIndex: 1
        }
      ],
      series: [
        {
          name: 'Candlestick',
          type: 'candlestick',
          data: this.chartData,
          itemStyle: {
            color: upColor,
            color0: downColor,
            borderColor: upColor,
            borderColor0: downColor,
            borderWidth: 1,
            borderColorDoji: '#888888'
          },
          barWidth: '70%',
          barMaxWidth: 20,
          barMinWidth: 4,
          barGap: '30%',
          barCategoryGap: '20%',
          emphasis: {
            scale: false,
            itemStyle: {
              borderWidth: 2
            }
          },
          tooltip: { show: true },
          xAxisIndex: 0,
          yAxisIndex: 0
        },
        {
          name: 'Volume',
          type: 'bar',
          data: this.volumeData.map((volume, index) => {
            const candleData = this.chartData[index];
            const isUp = candleData && candleData[1] >= candleData[0];
            return {
              value: volume,
              itemStyle: {
                color: isUp ? '#00FF5760' : '#FF000060',
                borderColor: 'transparent',
                borderWidth: 0
              }
            };
          }),
          xAxisIndex: 1,
          yAxisIndex: 1,
          barWidth: '70%',
          barMaxWidth: 20,
          barMinWidth: 4,
          barGap: '30%',
          barCategoryGap: '20%'
        }
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999'
          }
        },
        confine: false,
        alwaysShowContent: false,
        backgroundColor: 'rgba(50, 50, 50, 0.95)',
        borderColor: '#777',
        borderWidth: 1,
        padding: [8, 12],
        textStyle: {
          color: '#fff',
          fontSize: 11
        },
        // Custom formatter to include the full date/time and OHLCV details
        formatter: (params: any) => {
          try {
            if (!params || params.length === 0) return '';

            const idx = params[0].dataIndex;
            const labels = this.chartLabels || [];
            const iso = labels[idx] || null;
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            let dateStr = '';
            if (iso) {
              const d = new Date(iso);
              dateStr = d.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: tz
              });
            }

            // Find candlestick and volume params
            const candleParam = params.find((p: any) => p.seriesType === 'candlestick' || p.seriesName === 'Candlestick');
            const volParam = params.find((p: any) => p.seriesType === 'bar' || p.seriesName === 'Volume');

            const parts: string[] = [];
            if (dateStr) {
              parts.push(`<div style="font-weight:600;margin-bottom:8px;color:#00ff00;">${dateStr}</div>`);
            }

            if (candleParam && Array.isArray(candleParam.data)) {
              const [index, open, close, low, high] = candleParam.data;
              parts.push(`<div style="margin-bottom:2px;">O: ${open?.toFixed?.(2) ?? open}</div>`);
              parts.push(`<div style="margin-bottom:2px;">H: ${high?.toFixed?.(2) ?? high}</div>`);
              parts.push(`<div style="margin-bottom:2px;">L: ${low?.toFixed?.(2) ?? low}</div>`);
              parts.push(`<div style="margin-bottom:2px;">C: ${close?.toFixed?.(2) ?? close}</div>`);
            } else if (params[0].axisValue) {
              parts.push(String(params[0].axisValue));
            }

            if (volParam) {
              const vol = volParam.data;
              let volStr = '';
              if (vol.value >= 1000000) {
                volStr = (vol.value / 1000000).toFixed(2) + 'M';
              } else if (vol.value >= 1000) {
                volStr = (vol.value / 1000).toFixed(2) + 'K';
              } else {
                volStr = vol.value.toString();
              }
              parts.push(`<div style="margin-top:8px;color:#00ff57;">Vol: ${volStr}</div>`);
            }

            return parts.join('');
          } catch (e) {
            // Fallback to default display on error
            return '';
          }
        }
      },
      toolbox: {
        show: false,
        right: 20,
        top: 20,
        feature: {
          dataZoom: {
            yAxisIndex: false,
            title: {
              zoom: 'Zoom',
              back: 'Reset Zoom'
            }
          },
          brush: {
            type: ['rect', 'polygon', 'lineX', 'lineY', 'keep', 'clear'],
            title: {
              rect: 'Rectangle Select',
              polygon: 'Polygon Select',
              lineX: 'Horizontal Select',
              lineY: 'Vertical Select',
              keep: 'Keep Previous',
              clear: 'Clear Selection'
            }
          },
          restore: {
            title: 'Reset'
          }
        },
        iconStyle: {
          borderColor: '#9aa0a6'
        },
        emphasis: {
          iconStyle: {
            borderColor: '#666'
          }
        }
      }
    };

    this.currentChart = echarts.init(container);
    this.currentChart.setOption(option);
    this.isChartLoaded = true;
    try {
      const totalCandles = this.currentData ? this.currentData.length : 0;
      const dz = this.currentChart.getOption().dataZoom && this.currentChart.getOption().dataZoom[0];
      const start = dz && typeof dz.start === 'number' ? dz.start : this.defaultZoomStart;
      const end = dz && typeof dz.end === 'number' ? dz.end : this.defaultZoomEnd;
      const visiblePercent = Math.max(0.1, (end - start));
      const visibleCandles = Math.max(1, Math.round((visiblePercent / 100) * totalCandles));
      this.adjustBarWidth(visibleCandles);
    } catch (e) {
    }

    this.currentChart.on && this.currentChart.on('datazoom', () => {
      try {
        const opt = this.currentChart.getOption();
        const dz0 = opt && opt.dataZoom && opt.dataZoom[0];
        const start = dz0 && typeof dz0.start === 'number' ? dz0.start : this.defaultZoomStart;
        const end = dz0 && typeof dz0.end === 'number' ? dz0.end : this.defaultZoomEnd;
        if (!this.currentData) return;
        const total = this.currentData.length;
        const visiblePercent = Math.max(0.1, (end - start));
        const visible = Math.max(1, Math.round((visiblePercent / 100) * total));
        this.adjustBarWidth(visible);
      } catch (e) {
      }
    });
    // Auto-zoom to latest data after chart initialization
    if (this.autoZoomToLatest) {
      setTimeout(() => {
        this.zoomToLatest(this.autoZoomCandleCount);
      }, 100);
    }
  }


  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId) => this.setupChart(stockId), (type, data) => this.emit(type, data));
  }



  private toggleTheme(): void {
    const widget = this.shadowRoot.querySelector('.traderverse-widget');
    const themeToggle = this.shadowRoot.querySelector('.theme-toggle');
    if (widget) {
      const currentTheme = widget.getAttribute('data-theme');
      const newTheme = currentTheme === 'black' ? 'white' : 'black';
      widget.setAttribute('data-theme', newTheme);
      this.config.theme = newTheme as 'white' | 'black';

      if (themeToggle) {
        themeToggle.textContent = newTheme === 'black' ? '☀️' : '🌙';
      }
      this.updateDisplay();
    }
  }

  private toggleFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      this.container.requestFullscreen?.();
    }
  }

  // Public API methods
  public on(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  public off(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  public setSymbol(stockId: string): void {
    this.changeSymbol(stockId);
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }



  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;

    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

    this.updateDisplay();
  }

  public getCurrentData(): CandleStickData[] | null {
    return this.currentData;
  }

  public toggleSeries(seriesName: string, visible: boolean): void {
    if (this.currentChart) {
      this.currentChart.dispatchAction({
        type: visible ? 'legendSelect' : 'legendUnSelect',
        name: seriesName
      });
    }
  }

  public addCustomSeries(seriesConfig: any): void {
    if (this.currentChart) {
      const currentOption = this.currentChart.getOption();
      const updatedSeries = [...currentOption.series, seriesConfig];
      this.currentChart.setOption({
        series: updatedSeries
      });
    }
  }

  public zoomToRange(startPercent: number, endPercent: number): void {
    if (this.currentChart) {
      this.currentChart.dispatchAction({
        type: 'dataZoom',
        start: startPercent,
        end: endPercent
      });

      // Calculate visible candles and adjust bar width
      if (this.currentData) {
        const totalCandles = this.currentData.length;
        const visiblePercent = endPercent - startPercent;
        const visibleCandles = Math.round((visiblePercent / 100) * totalCandles);
        this.adjustBarWidth(visibleCandles);
      }
    }
  }

  public resetZoom(): void {
    if (this.currentChart) {
      this.currentChart.dispatchAction({
        type: 'dataZoom',
        start: 0,
        end: 100
      });
    }
  }

  public enableZoom(enabled: boolean): void {
    if (this.currentChart) {
      this.currentChart.setOption({
        dataZoom: [{
          type: 'inside',
          disabled: !enabled
        }]
      });
    }
  }

  public simulateTradeUpdate(tradeData: any): void {
    const jsonString = typeof tradeData === 'string' ? tradeData : JSON.stringify(tradeData);
    this.handleOHLCVUpdate(jsonString);
  }

  public getChartState(): any {
    return {
      isChartLoaded: this.isChartLoaded,
      currentDataLength: this.currentData?.length || 0,
      chartDataLength: this.chartData.length,
      volumeDataLength: this.volumeData.length,
      currentSymbol: this.currentData?.[0]?.symbol || 'unknown',
      widgetConfig: pickCandlestickExportConfig(this.config),
    };
  }

  public setDefaultZoom(startPercent: number, endPercent: number): void {
    this.defaultZoomStart = Math.max(0, Math.min(100, startPercent));
    this.defaultZoomEnd = Math.max(0, Math.min(100, endPercent));

    if (this.currentChart) {
      this.currentChart.setOption({
        dataZoom: [
          {
            start: this.defaultZoomStart,
            end: this.defaultZoomEnd
          }
        ]
      });
    }
  }

  public setAutoZoomToLatest(enabled: boolean, candleCount: number = 50): void {
    this.autoZoomToLatest = enabled;
    this.autoZoomCandleCount = candleCount;
  }

  public setMaxDataPoints(maxPoints: number): void {
    this.maxDataPoints = Math.max(100, maxPoints); // Minimum 100 points
    this.trimDataArrays(); // Apply limit immediately
  }

  public setCandlestickStyle(upColor: string = '#00FF57', downColor: string = '#FF0000', thickness: 'thin' | 'normal' | 'thick' = 'thick'): void {
    if (!this.currentChart) return;

    let barWidth = '90%';
    let barMaxWidth = 40;
    let barMinWidth = 8;
    let borderWidth = 2;

    switch (thickness) {
      case 'thin':
        barWidth = '70%';
        barMaxWidth = 15;
        barMinWidth = 3;
        borderWidth = 1;
        break;
      case 'normal':
        barWidth = '80%';
        barMaxWidth = 25;
        barMinWidth = 5;
        borderWidth = 1;
        break;
      case 'thick':
        barWidth = '90%';
        barMaxWidth = 40;
        barMinWidth = 8;
        borderWidth = 2;
        break;
    }

    this.currentChart.setOption({
      series: [
        {
          itemStyle: {
            color: upColor,
            color0: downColor,
            borderColor: upColor,
            borderColor0: downColor,
            borderWidth: borderWidth
          },
          barWidth: barWidth,
          barMaxWidth: barMaxWidth,
          barMinWidth: barMinWidth
        }
      ]
    });
  }

  public zoomToRecent(minutes: number = 60): void {
    if (!this.currentData || this.currentData.length === 0) return;

    // Calculate how many data points represent the specified minutes
    const totalMinutes = this.currentData.length; // Assuming 1-minute candles
    const percentToShow = Math.min(100, (minutes / totalMinutes) * 100);
    const startPercent = Math.max(0, 100 - percentToShow);

    this.zoomToRange(startPercent, 100);
  }

  public zoomToLatest(candleCount: number = 50): void {
    if (!this.currentData || this.currentData.length === 0) return;

    // Calculate percentage based on number of candles to show
    const totalCandles = this.currentData.length;
    const percentToShow = Math.min(100, (candleCount / totalCandles) * 100);
    const startPercent = Math.max(0, 100 - percentToShow);

    this.zoomToRange(startPercent, 100);

    // Adjust bar width based on zoom level
    this.adjustBarWidth(candleCount);
  }

  private adjustBarWidth(visibleCandles: number): void {
    if (!this.currentChart) return;

    // Dynamic bar width calculation based on visible candles
    let barWidth = '70%';
    let barMaxWidth = 20;
    let barMinWidth = 4;
    let barGap = '30%';
    let barCategoryGap = '20%';
    let borderWidth = 1;

    if (visibleCandles <= 15) {
      barWidth = '80%';
      barMaxWidth = 35;
      barMinWidth = 8;
      barGap = '20%';
      barCategoryGap = '15%';
      borderWidth = 2;
    } else if (visibleCandles <= 25) {
      barWidth = '75%';
      barMaxWidth = 25;
      barMinWidth = 6;
      barGap = '25%';
      barCategoryGap = '18%';
      borderWidth = 1;
    } else if (visibleCandles <= 50) {
      barWidth = '70%';
      barMaxWidth = 20;
      barMinWidth = 4;
      barGap = '30%';
      barCategoryGap = '20%';
      borderWidth = 1;
    } else if (visibleCandles <= 100) {
      barWidth = '60%';
      barMaxWidth = 15;
      barMinWidth = 3;
      barGap = '40%';
      barCategoryGap = '25%';
      borderWidth = 1;
    } else if (visibleCandles <= 200) {
      barWidth = '50%';
      barMaxWidth = 10;
      barMinWidth = 2;
      barGap = '50%';
      barCategoryGap = '30%';
      borderWidth = 1;
    } else {
      barWidth = '40%';
      barMaxWidth = 8;
      barMinWidth = 1;
      barGap = '60%';
      barCategoryGap = '35%';
      borderWidth = 1;
    }

    // Update the chart with new bar settings
    this.currentChart.setOption({
      series: [
        {
          barWidth: barWidth,
          barMaxWidth: barMaxWidth,
          barMinWidth: barMinWidth,
          barGap: barGap,
          barCategoryGap: barCategoryGap,
          itemStyle: {
            borderWidth: borderWidth
          }
        },
        {
          barWidth: barWidth,
          barMaxWidth: barMaxWidth,
          barMinWidth: barMinWidth,
          barGap: barGap,
          barCategoryGap: barCategoryGap
        }
      ]
    });
  }

  public zoomPresets = {
    latest25: () => this.zoomToLatest(25),    // Show last 25 candles
    latest50: () => this.zoomToLatest(50),    // Show last 50 candles
    latest100: () => this.zoomToLatest(100),  // Show last 100 candles
    recent30min: () => this.zoomToRecent(30),
    recent1hour: () => this.zoomToRecent(60),
    recent2hours: () => this.zoomToRecent(120),
    recent4hours: () => this.zoomToRecent(240),
    showAll: () => this.resetZoom()
  };

  public getTimezoneInfo(): any {
    const now = new Date();
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      offset: now.getTimezoneOffset(),
      localTime: now.toLocaleString(),
      utcTime: now.toISOString(),
      offsetHours: Math.floor(Math.abs(now.getTimezoneOffset()) / 60),
      offsetMinutes: Math.abs(now.getTimezoneOffset()) % 60,
      offsetDirection: now.getTimezoneOffset() > 0 ? 'behind' : 'ahead'
    };
  }

  private areDataArraysConsistent(): boolean {
    if (!this.currentData) return true;

    const dataLength = this.currentData.length;
    return (
      this.chartData.length === dataLength &&
      this.volumeData.length === dataLength &&
      this.chartLabels.length === dataLength
    );
  }

  public destroy(): void {
    // Clear all timeouts and intervals
    if (this.updateInterval) {
      window.clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    if (this.requestStocksDataTimeout) {
      clearTimeout(this.requestStocksDataTimeout);
      this.requestStocksDataTimeout = null;
    }

    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    // Remove WebSocket event listeners
    // Remove this instance from centralized socket dispatcher
    try {
      const win = window as any;
      if (win.__TraderverseCandleStickChartInstances) {
        win.__TraderverseCandleStickChartInstances.delete(this);
        // If no instances remain, remove global socket handler
        if (win.__TraderverseCandleStickChartInstances.size === 0 && win.__TraderverseCandleStickChartSocketHandler && win.socket) {
          try {
            win.socket.off('tradeupdate', win.__TraderverseCandleStickChartSocketHandler);
          } catch (e) {
            // ignore
          }
          win.__TraderverseCandleStickChartSocketRegistered = false;
          win.__TraderverseCandleStickChartSocketHandler = null;
        }
      }
    } catch (e) {
      // ignore
    }

    // Dispose ECharts instance
    if (this.currentChart) {
      const echarts = (window as any).echarts;
      if (echarts) {
        try {
          echarts.dispose(this.currentChart);
        } catch (e) {
          console.warn('Error disposing chart:', e);
        }
      }
      this.currentChart = null;
    }

    // Disconnect ResizeObserver
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }

    // Clear data arrays to free memory
    this.currentData = null;
    this.chartData = [];
    this.volumeData = [];
    this.chartLabels = [];

    // Clear event handlers
    this.eventHandlers.clear();

    // Clear shadow DOM
    this.shadowRoot.innerHTML = '';

    // Cleanup any registered pollers for symbols
    try {
      const win = window as any;
      if (win.__TraderverseStockPollers) {
        for (const sym in win.__TraderverseStockPollers) {
          const p = win.__TraderverseStockPollers[sym];
          if (p && p.instances) {
            p.instances.delete(this);
            if (p.instances.size === 0) {
              if (p.intervalId) {
                clearInterval(p.intervalId);
              }
              delete win.__TraderverseStockPollers[sym];
            }
          }
        }
      }
    } catch (e) {
      // ignore
    }
  }



  private formatLocalTime(date: Date): string {
    const interval = this.config.interval || '1D';
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // For intraday intervals (minutes/hours), show time
    if (interval.includes('m') || interval.includes('h')) {
      // For very short intervals (1m, 5m, 15m), show only time
      if (interval === '1m' || interval === '5m' || interval === '15m') {
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: false,
          timeZone: tz
        });
      }
      // For longer intraday intervals, show date and time
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
        timeZone: tz
      });
    }

    // For daily intervals and longer, show date only
    return date.toLocaleDateString('en-US', {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
      timeZone: tz
    });
  }

  private findInsertIndex(newTime: Date): number {
    if (!this.currentData || this.currentData.length === 0) return 0;

    const newTimeMs = newTime.getTime();
    for (let i = this.currentData.length - 1; i >= 0; i--) {
      if ((this.currentData[i].time * 1000) <= newTimeMs) {
        return i + 1;
      }
    }
    return 0;
  }

  private isValidTimestamp(timestamp: number): boolean {
    const now = Date.now();
    const oneYearAgo = now - (365 * 24 * 60 * 60 * 1000);
    const oneYearFromNow = now + (365 * 24 * 60 * 60 * 1000);

    return timestamp >= oneYearAgo && timestamp <= oneYearFromNow;
  }

  private shouldCreateNewCandle(newTime: Date): { shouldCreate: boolean, updateIndex: number } {
    if (!this.currentData || this.currentData.length === 0) {
      return { shouldCreate: true, updateIndex: -1 };
    }

    const lastCandle = this.currentData[this.currentData.length - 1];
    const lastCandleTime = new Date(lastCandle.time * 1000);

    // Check if the times are exactly the same (same minute boundary)
    const isSameMinute = lastCandleTime.getTime() === newTime.getTime();

    if (isSameMinute) {
      return { shouldCreate: false, updateIndex: this.currentData.length - 1 };
    }

    // If new time is later, we need a new candle
    if (newTime.getTime() > lastCandleTime.getTime()) {
      return { shouldCreate: true, updateIndex: -1 };
    }

    // If new time is earlier, find the appropriate candle to update
    for (let i = this.currentData.length - 1; i >= 0; i--) {
      const candleTime = new Date(this.currentData[i].time * 1000);
      if (candleTime.getTime() === newTime.getTime()) {
        return { shouldCreate: false, updateIndex: i };
      }
    }

    // If no matching candle found, create new one
    return { shouldCreate: true, updateIndex: -1 };
  }
  
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const event: WidgetEvent = { type, data };
      handlers.forEach(handler => handler(event));
    }
  }
  
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

// Global registration for script tag usage
declare global {
  interface Window {
    TraderverseCandleStickChart: typeof TraderverseCandleStickChart;
  }
}

if (typeof window !== 'undefined') {
  window.TraderverseCandleStickChart = TraderverseCandleStickChart;
}